"use client";

import { cn } from "@/src/shared/utils/mergeTwClasses";
import { Box } from "@mui/material";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { createContext, forwardRef, useCallback, useContext, useEffect, useState } from "react";
import { CarouselApi, CarouselContextProps, CarouselWrapperProps } from "../lib/type";

const CarouselContext = createContext<CarouselContextProps | null>(null);

export function useCarousel() {
    const context = useContext(CarouselContext);

    if (!context) {
        throw new Error("useCarousel must be used within a <Carousel />");
    }

    return context;
}

const CarouselWrapper = forwardRef<HTMLDivElement, CarouselWrapperProps>(
    (
        {
            orientation = "horizontal",
            opts,
            setApi,
            className,
            children,
            autoPlayOptions,
            ...props
        },
        ref,
    ) => {
        const [carouselRef, api] = useEmblaCarousel(
            {
                ...opts,
                loop: true,
                axis: orientation === "horizontal" ? "x" : "y",
            },
            [
                Autoplay({
                    delay: 5000,
                    playOnInit: true,
                    stopOnInteraction: false,
                    ...autoPlayOptions,
                }),
            ],
        );
        const [canScrollPrev, setCanScrollPrev] = useState(false);
        const [canScrollNext, setCanScrollNext] = useState(false);

        const onSelect = useCallback((api: CarouselApi) => {
            if (!api) {
                return;
            }

            setCanScrollPrev(api.canScrollPrev());
            setCanScrollNext(api.canScrollNext());
        }, []);

        const scrollPrev = useCallback(() => {
            api?.scrollPrev();
        }, [api]);

        const scrollNext = useCallback(() => {
            api?.scrollNext();
        }, [api]);

        const handleKeyDown = useCallback(
            (event: React.KeyboardEvent<HTMLDivElement>) => {
                if (event.key === "ArrowLeft") {
                    event.preventDefault();
                    scrollPrev();
                } else if (event.key === "ArrowRight") {
                    event.preventDefault();
                    scrollNext();
                }
            },
            [scrollPrev, scrollNext],
        );

        useEffect(() => {
            if (!api || !setApi) {
                return;
            }

            setApi(api);
        }, [api, setApi]);

        useEffect(() => {
            if (!api) {
                return;
            }

            onSelect(api);
            api.on("reInit", onSelect);
            api.on("select", onSelect);

            return () => {
                api?.off("select", onSelect);
            };
        }, [api, onSelect]);

        return (
            <CarouselContext.Provider
                value={{
                    carouselRef,
                    api,
                    opts,
                    orientation: orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
                    scrollPrev,
                    scrollNext,
                    canScrollPrev,
                    canScrollNext,
                }}
            >
                <Box
                    ref={ref}
                    onKeyDownCapture={handleKeyDown}
                    className={cn("relative w-full", className)}
                    role={"region"}
                    aria-roledescription={"carousel"}
                    {...props}
                >
                    {children}
                </Box>
            </CarouselContext.Provider>
        );
    },
);

CarouselWrapper.displayName = "CarouselWrapper";
export { CarouselWrapper };
